import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, ResponsiveTableWrap, AdminEmptyState } from "@/components/admin/crud-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Star } from "lucide-react";
import { ImportDefaultsButton } from "@/components/admin/import-defaults-button";
import { ImageUploadField } from "@/components/admin/image-upload-field";
import { buildTestimonialSeeds } from "@/lib/admin-seeds";

export const Route = createFileRoute("/_authenticated/admin/testimonials")({ component: TestimonialsAdmin });

type Testimonial = { id: string; name: string; role: string | null; company: string | null; avatar_url: string | null; quote: string; rating: number; published: boolean };

function TestimonialsAdmin() {
  const { data, isLoading } = useResourceList<Testimonial>("testimonials");
  const { create, update, remove } = useResourceMutations("testimonials");
  const [editing, setEditing] = useState<Testimonial | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data ?? [];
    return (data ?? []).filter((t) =>
      [t.name, t.role, t.company, t.quote].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [data, search]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Testimonials"
        description="Customer quotes used across the site."
        onNew={() => { setEditing(null); setOpen(true); }}
        newLabel="New testimonial"
        actions={<ImportDefaultsButton table="testimonials" onConflict="name" loadRows={buildTestimonialSeeds} />}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, company or quote…"
        count={rows.length}
      />
      {!isLoading && !rows.length ? (
        <AdminEmptyState title={search ? "No testimonials match" : "No testimonials yet"} />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead className="hidden sm:table-cell">Rating</TableHead>
                  <TableHead className="hidden md:table-cell">Quote</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
                {rows.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{[t.role, t.company].filter(Boolean).join(" · ") || "—"}</div>
                      <div className="mt-1 flex sm:hidden">
                        {Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />)}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell max-w-sm">
                      <div className="line-clamp-2 text-xs text-muted-foreground">{t.quote}</div>
                    </TableCell>
                    <TableCell><Badge variant={t.published ? "default" : "secondary"}>{t.published ? "Live" : "Hidden"}</Badge></TableCell>
                    <TableCell className="text-right">
                      <RowActions onEdit={() => { setEditing(t); setOpen(true); }} onDelete={() => { if (confirm("Delete?")) remove.mutate(t.id); }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTableWrap>
        </div>
      )}
      <TestimonialDialog
        key={editing?.id ?? "new"}
        open={open} onOpenChange={setOpen} editing={editing}
        onSubmit={(v: any) => {
          if (editing) update.mutate({ id: editing.id, values: v }, { onSuccess: () => setOpen(false) });
          else create.mutate(v, { onSuccess: () => setOpen(false) });
        }}
      />
    </div>
  );
}

function TestimonialDialog({ open, onOpenChange, editing, onSubmit }: any) {
  const [name, setName] = useState(editing?.name ?? "");
  const [role, setRole] = useState(editing?.role ?? "");
  const [company, setCompany] = useState(editing?.company ?? "");
  const [avatar, setAvatar] = useState(editing?.avatar_url ?? "");
  const [quote, setQuote] = useState(editing?.quote ?? "");
  const [rating, setRating] = useState<number>(editing?.rating ?? 5);
  const [published, setPublished] = useState(editing?.published ?? true);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit testimonial" : "New testimonial"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="grid gap-2"><Label>Rating (1–5)</Label><Input type="number" min={1} max={5} value={rating} onChange={(e) => setRating(Number(e.target.value))} /></div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2"><Label>Role</Label><Input value={role} onChange={(e) => setRole(e.target.value)} /></div>
            <div className="grid gap-2"><Label>Company</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} /></div>
          </div>
          <ImageUploadField
            label="Avatar"
            value={avatar || undefined}
            onChange={(url) => setAvatar(url ?? "")}
            folder="testimonials"
          />
          <div className="grid gap-2"><Label>Quote</Label><Textarea rows={3} value={quote} onChange={(e) => setQuote(e.target.value)} /></div>
          <div className="flex items-center gap-3"><Switch checked={published} onCheckedChange={setPublished} /><Label>Published</Label></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ name, role: role || null, company: company || null, avatar_url: avatar || null, quote, rating, published })}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
